import { Toggle as TogglePrimitive } from 'radix-ui';
import * as React from 'react';

// Type-only import so the `{@link ToggleGroup}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type ToggleGroup, type ToggleGroupItem } from './ToggleGroup';
import { VariantProps, cn, tv } from './utils/cn';

/**
 * `tailwind-variants` style factory powering {@link Toggle} and the items in
 * {@link ToggleGroup}. Exported so {@link ToggleGroup} can apply the same
 * visual contract to its {@link ToggleGroupItem} children.
 */
const toggleVariants = tv({
  base: "group/toggle inline-flex items-center justify-center gap-1 rounded-lg text-sm font-medium whitespace-nowrap transition-all outline-none hover:bg-muted hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 aria-pressed:bg-muted data-[state=on]:bg-muted dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  variants: {
    variant: {
      default: 'bg-transparent',
      outline: 'border border-input bg-transparent hover:bg-muted',
    },
    size: {
      default:
        'h-8 min-w-8 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
      sm: "h-7 min-w-7 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
      lg: 'h-9 min-w-9 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

/**
 * Two-state button that flips its `pressed` value on click. Use for a single
 * boolean preference rendered as a labelled chip or icon button (bold/italic
 * in a toolbar, "show archived", and so on).
 *
 * For a group of related toggles, use {@link ToggleGroup}.
 *
 * @example
 * <Toggle
 *   pressed={isBold}
 *   onPressedChange={setIsBold}
 *   aria-label="Bold"
 * >
 *   <Icon name="Bold" size="auto" />
 * </Toggle>
 */
function Toggle({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
